// src/views/EditarVehiculo.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson } from "../../../utils/http";

/**
 * EditarVehiculo
 * - Editables: Cliente (picker), Patente, Descripción/Modelo, Marca (combo).
 */

const VEHIC_BASE = (apiRoutes?.vehiculosConTarjeta?.list || "").replace(
  /\/list(?:\?.*)?$/,
  "",
);
const INIT_URL = (id) => `${VEHIC_BASE}/${encodeURIComponent(id)}/editar`;
const UPDATE_URL = (id) => `${VEHIC_BASE}/${encodeURIComponent(id)}/editar`;

export default function EditarVehiculo({
  id,
  vehiculo = null,
  onClose = () => {},
  onSaved = () => {},
}) {
  const pk = id ?? vehiculo?.id;

  // -------- estado base y combos --------
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [init, setInit] = useState(null); // { vehiculo, marcas }
  const v = init?.vehiculo || vehiculo || {};

  const [marcaOpts, setMarcaOpts] = useState([]); // [{id, nombre}]

  // -------- form (editables) --------
  const [clienteSel, setClienteSel] = useState(
    v?.clienteId
      ? {
          id: String(v.clienteId),
          codigo: v.clienteCodigo,
          nombre: v.clienteNombre,
        }
      : null,
  );
  const [patente, setPatente] = useState(v?.patente ?? "");
  const [descripcion, setDescripcion] = useState(
    v?.descripcionModelo ?? v?.modelo ?? "",
  );
  const [marcaId, setMarcaId] = useState(
    v?.marcaId != null ? String(v.marcaId) : "",
  );

  // -------- buscador de cliente (dropdown) --------
  const [openCliente, setOpenCliente] = useState(false);
  const [qCliente, setQCliente] = useState("");
  const [itemsCliente, setItemsCliente] = useState([]);
  const [loadingCliente, setLoadingCliente] = useState(false);
  const clienteBoxRef = useRef(null);

  // errores
  const [clienteError, setClienteError] = useState(false);
  const [marcaError, setMarcaError] = useState(false);

  // cierre al click afuera
  useEffect(() => {
    const handler = (e) => {
      if (clienteBoxRef.current && !clienteBoxRef.current.contains(e.target)) {
        setOpenCliente(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // autocomplete clientes
  useEffect(() => {
    if (!openCliente) return;
    const ac = new AbortController();

    const t = setTimeout(async () => {
      const term = (qCliente || "").trim();
      if (term.length < 2) {
        setItemsCliente([]);
        return;
      }
      try {
        setLoadingCliente(true);
        const p = new URLSearchParams({
          q: term,
          page: 0,
          size: 10,
          tipo: "codigoNombre",
        });
        const url = `${apiRoutes?.clientes?.autocomplete}?${p.toString()}`;
        const j = await apiFetchJson(url, { signal: ac.signal });
        const arr = Array.isArray(j?.items) ? j.items : [];
        setItemsCliente(
          arr.map((c) => ({
            id: String(c.id ?? c.codigo ?? c.clienteId ?? c.value ?? term),
            codigo: c.codigo ?? "",
            nombre: c.nombre ?? "",
          })),
        );
      } catch {
        if (!ac.signal.aborted) setItemsCliente([]);
      } finally {
        if (!ac.signal.aborted) setLoadingCliente(false);
      }
    }, 350);

    return () => {
      clearTimeout(t);
      ac.abort();
    };
  }, [qCliente, openCliente]);

  // 👇 Anti-doble fetch en DEV (React 18 StrictMode)
  const devRunRef = useRef(false);

  // -------- carga inicial (detalle + combos) --------
  useEffect(() => {
    if (!pk) return;

    // Evitar la primera ejecución extra de StrictMode en DEV
    if (process.env.NODE_ENV !== "production") {
      if (!devRunRef.current) {
        devRunRef.current = true;
        return; // salta esta pasada; la segunda sí hará el fetch
      }
    }

    let alive = true;
    const ac = new AbortController();

    (async () => {
      try {
        setLoading(true);

        const data = await apiFetchJson(INIT_URL(pk), { signal: ac.signal });
        if (!alive || ac.signal.aborted) return;

        const veh = data?.vehiculo ?? vehiculo ?? {};
        const marcas = Array.isArray(data?.marcas) ? data.marcas : [];

        setInit({ vehiculo: veh, marcas });
        setMarcaOpts(
          marcas.map((m) => ({
            id: String(m.id ?? m.value ?? ""),
            nombre: m.nombre ?? m.label ?? "",
          })),
        );

        setClienteSel(
          veh?.clienteId
            ? {
                id: String(veh.clienteId),
                codigo: veh.clienteCodigo,
                nombre: veh.clienteNombre,
              }
            : null,
        );

        setPatente(veh?.patente ?? "");
        setDescripcion(veh?.descripcionModelo ?? veh?.modelo ?? "");
        setMarcaId(veh?.marcaId != null ? String(veh.marcaId) : "");
      } catch {
        if (!ac.signal.aborted) setInit({ vehiculo: vehiculo || {} });
      } finally {
        if (alive && !ac.signal.aborted) setLoading(false);
      }
    })();

    return () => {
      alive = false;
      ac.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pk]);

  // -------- validaciones / dirty --------
  const requiredError = !clienteSel?.id || !marcaId;

  const isDirty = useMemo(() => {
    const orig = init?.vehiculo || {};
    const sameCliente =
      String(orig?.clienteId ?? "") === String(clienteSel?.id ?? "");
    const samePatente = (orig?.patente ?? "") === (patente ?? "");
    const sameDesc =
      (orig?.descripcionModelo ?? orig?.modelo ?? "") === (descripcion ?? "");
    const sameMarca = String(orig?.marcaId ?? "") === String(marcaId ?? "");
    return !(sameCliente && samePatente && sameDesc && sameMarca);
  }, [init, clienteSel, patente, descripcion, marcaId]);

  // -------- guardar (PATCH) --------
  const onSave = async () => {
    // Obligatorios
    if (!clienteSel?.id) {
      setClienteError(true);
      clienteBoxRef.current?.querySelector("input")?.focus();
      return;
    }
    if (!marcaId) {
      setMarcaError(true);
      document.getElementById("marcaSelect")?.focus();
      return;
    }
    if (!pk || !isDirty) return;

    const orig = init?.vehiculo || {};

    // SIEMPRE los requeridos por backend
    const payload = {
      clienteId: Number(clienteSel.id) || clienteSel.id,
      marcaId: Number(marcaId) || marcaId,
    };

    // Solo lo que cambió
    if ((orig?.patente ?? "") !== (patente ?? "")) {
      payload.patente = (patente ?? "").trim();
    }
    const origDesc = orig?.descripcionModelo ?? orig?.modelo ?? "";
    if (origDesc !== (descripcion ?? "")) {
      payload.descripcionModelo = (descripcion ?? "").trim();
    }

    try {
      setSaving(true);
      await apiFetchJson(UPDATE_URL(pk), {
        method: "PATCH",
        headers: { "Content-Type": "application/json; charset=UTF-8" },
        body: JSON.stringify(payload),
      });

      const updated = { ...(init?.vehiculo || {}), ...payload };
      onSaved(updated);
      onClose();
    } catch (e) {
      alert("No se pudo guardar el vehículo. Revisá la consola.");
      console.error("[EditarVehiculo] save error:", e);
    } finally {
      setSaving(false);
    }
  };

  const requireClienteIfMissing = () => {
    if (!clienteSel?.id) setClienteError(true);
  };
  const requireMarcaIfMissing = () => {
    if (!marcaId) setMarcaError(true);
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="section-title">
            Introduzca los datos de Vehículos con Tarjetas
          </h2>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="text-center py-12">Cargando...</div>
          ) : (
            <div className="form-grid">
              {/* Cliente */}
              <div className="form-row" ref={clienteBoxRef}>
                <div className="form-label">
                  Cliente{" "}
                  <span className="text-red-600" title="Obligatorio">
                    *
                  </span>
                  {clienteError && (
                    <div className="text-red-600 text-sm mt-1">
                      Debés seleccionar un cliente.
                    </div>
                  )}
                </div>
                <div className="flex-1 relative">
                  <div className="flex gap-2">
                    <input
                      className={`input ${clienteError ? "border-2 border-red-500 focus:border-red-600 ring-0 focus:ring-0 shadow-none bg-white" : ""}`}
                      placeholder="Código o nombre…"
                      onFocus={() => setOpenCliente(true)}
                      aria-required="true"
                      aria-invalid={clienteError ? "true" : "false"}
                      value={
                        openCliente
                          ? qCliente
                          : clienteSel
                            ? `${clienteSel.codigo ?? ""} - ${clienteSel.nombre ?? ""}`.trim()
                            : ""
                      }
                      onChange={(e) => {
                        setOpenCliente(true);
                        setQCliente(e.target.value);
                        if (clienteError) setClienteError(false);
                      }}
                    />
                  </div>

                  {openCliente && (
                    <div className="dropdown-panel">
                      {loadingCliente && (
                        <div className="px-3 py-2 text-sm text-gray-500">
                          Buscando…
                        </div>
                      )}
                      {!loadingCliente &&
                        itemsCliente.map((c) => (
                          <div
                            key={`${c.id}-${c.codigo}`}
                            className="dropdown-item"
                            onClick={() => {
                              setClienteSel(c);
                              setOpenCliente(false);
                              setQCliente("");
                              setClienteError(false);
                            }}
                          >
                            {c.codigo} — {c.nombre}
                          </div>
                        ))}
                      {!loadingCliente && itemsCliente.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-400">
                          Escribí al menos 2 caracteres…
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Patente */}
              <div className="form-row">
                <div className="form-label">Patente:</div>
                <div className="flex-1 relative">
                  <input
                    className="input"
                    value={patente}
                    onFocus={() => {
                      requireClienteIfMissing();
                      requireMarcaIfMissing();
                    }}
                    onChange={(e) => setPatente(e.target.value)}
                    placeholder="AAA123 o ABBB123"
                  />
                </div>
              </div>

              {/* Descripción/Modelo */}
              <div className="form-row">
                <div className="form-label">Descripción/Modelo:</div>
                <div className="flex-1 relative">
                  <textarea
                    className="input"
                    rows={1}
                    value={descripcion}
                    onFocus={() => {
                      requireClienteIfMissing();
                      requireMarcaIfMissing();
                    }}
                    onChange={(e) => setDescripcion(e.target.value)}
                    placeholder="Modelo / descripción del vehículo"
                  />
                </div>
              </div>

              {/* Marca */}
              <div className="form-row">
                <div className="form-label">
                  Marca{" "}
                  <span className="text-red-600" title="Obligatorio">
                    *
                  </span>
                  {marcaError && (
                    <div className="text-red-600 text-sm mt-1">
                      Debés seleccionar una marca.
                    </div>
                  )}
                </div>
                <div className="flex-1 relative">
                  <select
                    id="marcaSelect"
                    className={`input ${marcaError ? "border-2 border-red-500 focus:border-red-600 ring-0 focus:ring-0 shadow-none bg-white" : ""}`}
                    value={marcaId}
                    onFocus={requireClienteIfMissing}
                    onChange={(e) => {
                      setMarcaId(e.target.value);
                      if (marcaError) setMarcaError(false);
                    }}
                    aria-required="true"
                    aria-invalid={marcaError ? "true" : "false"}
                    required
                  >
                    <option value="">— Seleccionar —</option>
                    {marcaOpts.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.nombre}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <div className="flex justify-start gap-2">
            <button
              className="btn-fil disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={saving || requiredError || !isDirty}
              onClick={onSave}
            >
              {saving ? "Guardando..." : "Guardar"}
            </button>
            <button className="btn-fil" type="button" onClick={onClose}>
              Consultar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
